import { ReactElement, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";

export interface QualityControlFormProps {
  onClose?: () => void;
  onSubmit?: (data: QualityControlData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface QualityControlData {
  filterType: string;
  filterValue: string;
}

const QualityControlForm = ({
  onClose,
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: QualityControlFormProps): ReactElement => {
  const [formData, setFormData] = useState<QualityControlData>({
    filterType: "starts_with",
    filterValue: "MT-",
  });

  const handleInputChange = (
    field: keyof QualityControlData,
    value: string | number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit?.(formData);
  };

  const filterTypeOptions = [
    { value: "starts_with", label: "Starts with" },
    { value: "regexp", label: "Regexp" },
  ];

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <Typography
        variant="body2"
        className="text-gray-600 mb-6 leading-relaxed"
        sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
      >
        Enter the mitochondrial gene prefix or regular expression used to
        calculate QC metrics. The default prefix is MT-. QC results include
        violin plots for genes per cell (n_genes_by_counts), total counts,
        mitochondrial content (pct_counts_mt), and a total-counts versus
        detected-genes scatter plot. Use these plots to choose filtering
        thresholds in the next step.
      </Typography>

      <Divider className="mb-6" />

      {/* Form Fields */}
      <div className="space-y-6 flex-1">
        {/* Filter Type */}
        <FormControl fullWidth>
          <InputLabel id="filter-type-label">Filter Type</InputLabel>
          <Select
            labelId="filter-type-label"
            value={formData.filterType}
            label="Filter Type"
            onChange={(e) => handleInputChange("filterType", e.target.value)}
            sx={{
              backgroundColor: "#FFFFFF", // White background for input
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D9D9D9", // tokens.colors.gray[300]
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#19C1DB", // tokens.colors.brand.primary
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#19C1DB", // tokens.colors.brand.primary
              },
            }}
          >
            {filterTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Mitochondrial gene pattern */}
        <TextField
          fullWidth
          label="Mitochondrial gene pattern"
          value={formData.filterValue}
          onChange={(e) => handleInputChange("filterValue", e.target.value)}
          helperText="Default: MT- (for example, MT-ND1 and MT-CO1)"
          sx={{
            backgroundColor: "#FFFFFF", // White background for input
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9", // tokens.colors.gray[300]
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB", // tokens.colors.brand.primary
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB", // tokens.colors.brand.primary
              },
            },
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between gap-3 mt-6 sticky bottom-0 bg-[#F5F5F5] pt-3">
        {onBack && (
          <Button variant="outlined" onClick={onBack} sx={{ textTransform: "none", px: 3, py: 1.5 }}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default QualityControlForm;
